import { Component, Input, OnChanges, SimpleChanges } from '@angular/core'
import { doc, Firestore, serverTimestamp, updateDoc } from '@angular/fire/firestore'
import { AdminClient, CrmAssignee, FirestoreStructure } from '@parkupp/core'

enum NonRenewalReason {
    FOUND_ALTERNATIVE = 'Found Alternative Parking',
    TOO_EXPENSIVE = 'Too Expensive',
    NO_LONGER_NEEDED = 'No Longer Needs Parking',
    RELOCATED = 'Relocated',
    POOR_EXPERIENCE = 'Poor Experience',
    OTHER = 'Other',
}
import { ExpiringLeaseRow } from '../expiring-leases/expiring-leases.component'
import { AdminClientService } from 'src/app/services/admin-client.service'
import Swal from 'sweetalert2'

@Component({
    selector: 'app-expiring-leases-table',
    templateUrl: './expiring-leases-table.component.html',
    styleUrls: ['./expiring-leases-table.component.scss'],
})
export class ExpiringLeasesTableComponent implements OnChanges {
    @Input() expiringRows: ExpiringLeaseRow[] = []
    @Input() expiredRows: ExpiringLeaseRow[] = []
    @Input() crmAssignees: CrmAssignee[] = []
    @Input() crmAssigneeMap: { [key: string]: CrmAssignee } = {}

    Math = Math
    nonRenewalReasons = Object.values(NonRenewalReason)
    showLeadDetail = false
    selectedLead: AdminClient | null = null
    selectedRow: ExpiringLeaseRow | null = null
    selectedDetailTab = 'overview'

    constructor(
        private firestore: Firestore,
        private adminClientService: AdminClientService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (this.selectedLead && this.selectedLead.$key && this.selectedRow) {
            const allRows = [...this.expiringRows, ...this.expiredRows]
            const subKey = this.selectedRow.subscriptionKey
            const updated = allRows.find(
                (r) => r.adminClient.$key === this.selectedLead!.$key && r.subscriptionKey === subKey
            )
            if (updated) {
                this.selectedLead = updated.adminClient
                this.selectedRow = updated
            }
        }
    }

    openLeadDetail(row: ExpiringLeaseRow): void {
        this.selectedLead = row.adminClient
        this.selectedRow = row
        this.showLeadDetail = true
        this.selectedDetailTab = 'overview'
    }

    closeLeadDetail(): void {
        this.showLeadDetail = false
        this.selectedLead = null
        this.selectedRow = null
    }

    async updateLeadField(field: string, value: any): Promise<void> {
        if (!this.selectedLead) return
        ;(this.selectedLead as any)[field] = value
        await this.adminClientService.update(this.selectedLead)
    }

    async updateNonRenewalReason(reason: string | null): Promise<void> {
        if (!this.selectedLead?.$key || !this.selectedRow) return

        const reasonValue = reason || null
        const clientKey = this.selectedLead.$key
        const subscriptionKey = this.selectedRow.subscriptionKey

        // Update in-memory immediately so the UI reflects the change
        ;(this.selectedLead as any).nonRenewalReason = reasonValue
        this.selectedRow.summary.nonRenewalReason = reason

        // Single write to admin client: top-level field + nested summary (so onSnapshot rebuilds correctly)
        const clientRef = doc(this.firestore, FirestoreStructure.ADMIN_CLIENTS, clientKey)
        const clientUpdate: Record<string, any> = {
            nonRenewalReason: reasonValue,
            updatedAt: serverTimestamp(),
        }
        if (subscriptionKey) {
            clientUpdate[`subscriptionSummaries.${subscriptionKey}.nonRenewalReason`] = reasonValue
        }
        await updateDoc(clientRef, clientUpdate)

        if (subscriptionKey) {
            const subRef = doc(this.firestore, `${FirestoreStructure.SUBSCRIPTIONS}/${subscriptionKey}`)
            await updateDoc(subRef, { nonRenewalReason: reasonValue })
        }
    }

    getUrgencyClass(days: number): string {
        if (days <= 3) return 'urgency--critical'
        if (days <= 7) return 'urgency--warning'
        return 'urgency--normal'
    }

    getAssigneeDisplayName(userResponsible: string | undefined): string {
        if (!userResponsible) return '-'
        const assignee = this.crmAssigneeMap[userResponsible]
        return assignee ? assignee.displayName : '-'
    }

    getAssigneeBadgeColor(userResponsible: string | undefined): string {
        if (!userResponsible) return '#6c757d'
        const assignee = this.crmAssigneeMap[userResponsible]
        return assignee?.badgeColor || '#6c757d'
    }

    formatDate(value: any): string {
        const date = this.toDate(value)
        if (!date) return '-'
        return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
    }

    getLatestCommentText(client: AdminClient): string {
        const comment = (client as any).latestComment
        if (!comment?.text) return ''
        return comment.text.length > 60 ? comment.text.substring(0, 60) + '...' : comment.text
    }

    callPhone(phoneNumber: string): void {
        window.open(`tel:${phoneNumber.replace(/\s+/g, '')}`, '_blank')
    }

    sendWhatsApp(phoneNumber: string): void {
        window.open(`https://wa.me/${phoneNumber.replace(/\s+/g, '')}`, '_blank')
    }

    sendEmail(email: string): void {
        window.open(`mailto:${email}`, '_blank')
    }

    getSubscriptionSummaries(): any[] {
        const summaries = (this.selectedLead as any)?.subscriptionSummaries
        if (!summaries) return []
        return Object.entries(summaries)
            .map(([key, value]: [string, any]) => ({ key, ...value }))
            .sort((a, b) => {
                const aTime = a.createdAt?.seconds || (a.createdAt instanceof Date ? a.createdAt.getTime() / 1000 : 0)
                const bTime = b.createdAt?.seconds || (b.createdAt instanceof Date ? b.createdAt.getTime() / 1000 : 0)
                return bTime - aTime
            })
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'Active': return 'sub-status-badge--active'
            case 'Completed': return 'sub-status-badge--completed'
            case 'Client Breached Non Payment': return 'sub-status-badge--breached'
            case 'Client Cancelled': case 'Merchant Rejected': case 'Expired': case 'Mutually Terminated': return 'sub-status-badge--cancelled'
            case 'Payment Initiated': case 'Awaiting Approval': case 'Accepted – payment processing': case 'Pending Client Approval': case 'Renewing': return 'sub-status-badge--pending'
            default: return 'sub-status-badge--default'
        }
    }

    private toDate(value: any): Date | null {
        if (!value) return null
        if (value instanceof Date) return value
        if (typeof value.toDate === 'function') return value.toDate()
        if (value.seconds) return new Date(value.seconds * 1000)
        if (typeof value === 'string') return new Date(value)
        return null
    }
}
